import { useQuery } from "@tanstack/react-query";
import { RecordManagement } from "app/class/record-management";
import { useColumns } from "app/hooks/useColumns";
import { useTables } from "app/hooks/useTables";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import queryKeys, { NEW_RECORD_ID } from "shared/services/queryKeys";

// Formato real devolvido por RecordManagement#get: o registro vive em `data`.
export interface RecordEnvelope<T = Record<string, any>> {
    success?: boolean;
    data: T;
    meta?: Record<string, any>;
}

export function useRecordProducerQuery(
    tableName: string,
    sysId?: string
) {
    const { i18n } = useTranslation();
    const tables = useTables();
    const columns = useColumns();

    const currentTable = useMemo(
        () => tables.data?.find(t => t.name === tableName) ?? null,
        [tables.data, tableName]
    );

    const currentColumns = useMemo(
        () => {
            let filteredColumns = columns.data?.filter(c => c.sys_table_id === currentTable?.id).sort((c, c2) => c.order - c2.order) ?? [];
            return filteredColumns;
        },
        [columns.data, currentTable]
    );

    const recordQuery = useQuery({
        queryKey: [...queryKeys.records.detail(currentTable?.name || "", sysId || NEW_RECORD_ID), i18n.language],
        enabled: !!currentTable?.name && !!sysId,
        queryFn: async (): Promise<RecordEnvelope | null> => {
            if (!currentTable || !sysId) return null;
            return await new RecordManagement<RecordEnvelope>(currentTable.name).translationLanguage(i18n.language).get(sysId);
        },
        refetchOnMount: "always",
        retry: (failureCount, error) => (error as any)?.response?.status !== 403 && failureCount < 3,
    });

    return {
        table: currentTable,
        columns: currentColumns,
        record: recordQuery.data,
        isLoading: recordQuery.isLoading || tables.isLoading || columns.isLoading,
        isError: recordQuery.isError,
        error: recordQuery.error,
        refetch: recordQuery.refetch,
        dataUpdatedAt: recordQuery.dataUpdatedAt,
        isReady: !!currentTable?.id && (!sysId || !!recordQuery.data),
    };
}
